import { useRef, useEffect, useState } from "react";

interface AutoCompleteProps {
  id: string;
  value?: string;
  inputClassName?: string;
  placeholder?: string;
  onChange: (value: string) => void;
  updateFormData: (streetAddress: string, city: string, state: string, postalCode: string, county: string, country: string) => void;
}

const AutoComplete = (props: AutoCompleteProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [address, setAddress] = useState('');
  const [defaultValue, setDefaultValue] = useState('');
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const autocomplete = useRef<any>(null);

  useEffect(() => {
    const checkGoogleMapsLoaded = () => {
      //@ts-ignore
      if (window.google && window.google.maps && window.google.maps.places) {
        setIsGoogleLoaded(true);
      } else {
        setTimeout(checkGoogleMapsLoaded, 100);
      }
    };

    checkGoogleMapsLoaded();
  }, []);

  useEffect(() => {
    if (isGoogleLoaded && inputRef.current && !autocomplete.current) {
      try {
        //@ts-ignore
        autocomplete.current = new window.google.maps.places.Autocomplete(inputRef.current, {
          fields: ["address_components", "formatted_address", "geometry", "name"],
          types: ["address"]
        });
        autocomplete.current.addListener('place_changed', handlePlaceSelect);

        // Ensure pac-container appears above modal
        const pacContainerStyle = document.createElement('style');
        pacContainerStyle.innerHTML = '.pac-container { z-index: 100010 !important; }';
        document.head.appendChild(pacContainerStyle);
      } catch (error) {
        console.error("Error initializing Google Maps Autocomplete:", error);
      }
    }
  }, [isGoogleLoaded]);

  useEffect(() => {
    if (!props.value) {
      setAddress('');
      setDefaultValue('');
    } else if (!defaultValue) {
      setAddress(props.value);
      setDefaultValue(props.value);
    }
  }, [props.value]);

  const handlePlaceSelect = () => {
    const place = autocomplete.current.getPlace();
    if (!place.address_components) {
      console.warn("No address components found");
      return;
    }
    const addressComponents = place.address_components;
    let streetAddress = '';
    let city = '';
    let state = '';
    let postalCode = '';
    let country = '';
    let county = '';

    for (const component of addressComponents) {
      const componentType = component.types[0];

      switch (componentType) {
        case 'street_number':
          streetAddress = component.long_name;
          break;
        case 'route':
          streetAddress += ' ' + component.long_name;
          break;
        case 'locality':
          city = component.long_name;
          break;
        case 'administrative_area_level_1':
          state = component.short_name;
          break;
        case 'administrative_area_level_2':
          county = component.long_name;
          break;
        case 'postal_code':
          postalCode = component.long_name;
          break;
        case 'country':
          country = component.long_name;
          break;
        default:
          break;
      }
    }

    // Build full formatted address for display
    const fullAddress = [streetAddress, city, state, postalCode, country]
      .filter(Boolean)
      .join(', ');

    setAddress(fullAddress);
    props.updateFormData(streetAddress, city, state, postalCode, county, country);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    props.onChange(event.target.value);
    setAddress(event.target.value);
  };

  return (
    <div className="w-100">
      {isGoogleLoaded ? (
        <input
          type="text"
          className={`form-control ${props.inputClassName || ''}`}
          id={props.id}
          placeholder={props.placeholder || "123 Main Street..."}
          ref={inputRef}
          value={address}
          onChange={(e) => { handleChange(e) }}
        />
      ) : (
        <p className="text-center w-100">Loading...</p>
      )}
    </div>
  );
};

export default AutoComplete;
